import { VideoResolution } from '@src/types.ts/video';

import type { StoryVideo } from '@src/types.ts/story';

/**
 * Pick the highest-quality resolution level whose rendition actually exists for a
 * source video. These are the same `mp4Url*` fields that
 * `VideoCreatorStore.getValidVideoSourceUrlForResLevel()` resolves, so publishing at
 * the returned level is guaranteed a real, audio-corrected rendition rather than
 * falling through to the raw original upload. Effectively the inverse of the resolver.
 *
 * Falls back to `Original` (the raw upload — the only remaining source) when no
 * rendition exists at all. Returning `HD` here would be wrong: `mp4UrlHd` was already
 * tested and absent.
 */
export function pickHighestAvailableResolutionLevel(
  video: StoryVideo['video'] | undefined,
): VideoResolution {
  if (video?.mp4UrlHd) return VideoResolution.HD;
  if (video?.mp4UrlHigh) return VideoResolution.High;
  if (video?.mp4UrlMedium) return VideoResolution.Medium;
  if (video?.mp4UrlLow) return VideoResolution.Low;
  return VideoResolution.Original;
}

/**
 * Resolve the resolution level to actually publish at. An explicit `Original`
 * selection must not publish the raw, unprocessed upload: the backend points
 * `originalVideo.url` at `unprocessed_original.mp4` whenever it exists (see
 * arbor-v1 `pages/api/story/[storyId]/populate.ts`), and that file lacks the
 * audio correction baked into the renditions. So `Original` is remapped to the
 * highest available processed rendition (HD → High → Medium → Low → raw
 * original as last resort). Any other level passes through unchanged.
 */
export function resolvePublishResolutionLevel(
  resLevel: VideoResolution,
  video: StoryVideo['video'] | undefined,
): VideoResolution {
  return resLevel === VideoResolution.Original
    ? pickHighestAvailableResolutionLevel(video)
    : resLevel;
}

/**
 * Human-readable label per resolution level, shared by the Export menu's
 * resolution picker, the publish progress drawer, the Downloads tab, and the
 * last-published info line so a record's resolution always reads the same
 * everywhere (e.g. "High (720p)", never the raw enum value "high"). Handles
 * both formats it is fed: the `VideoResolution` enum on `PublishRecord`, and
 * the legacy pixel-height string the render lambdas persist in
 * `lastPublishJson.resolution` (`${min(width,height)}p`). Unknown values
 * (e.g. an Original render's "1440p") pass through unchanged.
 */
const RESOLUTION_LABELS: Record<string, string> = {
  [VideoResolution.Low]: 'Low (270p)',
  [VideoResolution.Medium]: 'Medium (480p)',
  [VideoResolution.High]: 'High (720p)',
  [VideoResolution.HD]: 'HD (1080p)',
  [VideoResolution.Original]: 'Original',
  // Legacy pixel-height format from `lastPublishJson.resolution`.
  '270p': 'Low (270p)',
  '480p': 'Medium (480p)',
  '720p': 'High (720p)',
  '1080p': 'HD (1080p)',
};

export function formatResolutionLabel(resolution: string): string {
  return RESOLUTION_LABELS[resolution] ?? resolution;
}

/**
 * Bare pixel-height label for space-constrained meta lines (the clip cards'
 * "Published … · 1:1 · 270p"). Enum values map to their pixel height; legacy
 * pixel strings ("270p") and unknown values are already short and pass through.
 */
const SHORT_RESOLUTION_LABELS: Record<string, string> = {
  [VideoResolution.Low]: '270p',
  [VideoResolution.Medium]: '480p',
  [VideoResolution.High]: '720p',
  [VideoResolution.HD]: '1080p',
  [VideoResolution.Original]: 'Original',
};

export function formatResolutionShortLabel(resolution: string): string {
  return SHORT_RESOLUTION_LABELS[resolution] ?? resolution;
}
